import React from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Grid, Switch,
  FormControlLabel, Divider, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import * as ETVConstants from '../ETVConstants';

// List of Islamic countries (you can expand this list)
const ISLAMIC_COUNTRIES = ['SG','SA','AE','ID','MY','PK','EG','IR','IQ','TR','MA','DZ','SD'];

const getRestrictionOptions = (countryCode) => {
  const isIslamic = ISLAMIC_COUNTRIES.includes((countryCode || '').toUpperCase());

  const options = {
    travel_direction: {
      label: ETVConstants.trans('travel_direction'),
      description: ETVConstants.trans('travel_direction_expl'),
      values: [
        { label: 'Up', value: 'up' },
        { label: 'Down', value: 'down' },
      ]
    },
    weather: {
      label: ETVConstants.trans('weather'),
      description: ETVConstants.trans('weather_expl'),
      values: [
        { label: 'Clear', value: 'clear' },
        { label: 'Few Clouds', value: 'few_clouds' },
        { label: 'Clouds', value: 'clouds' },
        { label: 'Rain', value: 'rain' },
        { label: 'Drizzle', value: 'drizzle' },
        { label: 'Thunderstorm', value: 'thunderstorm' },
        { label: 'Snow', value: 'snow' },
        { label: 'Atmosphere', value: 'atmosphere' },
      ]
    }
  };

  if (isIslamic) {
    options.prayer_times = {
      label: ETVConstants.trans('prayer_times'),
      description: ETVConstants.trans('prayer_times_expl'),
      values: [
        { label: 'Fajr', value: 'Fajr' },
        { label: 'Dhuhr', value: 'Dhuhr' },
        { label: 'Asr', value: 'Asr' },
        { label: 'Maghrib', value: 'Maghrib' },
        { label: 'Isha', value: 'Isha' },
      ]
    };
  }

  return options;
};

class RestrictionSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      restrictions: {},
      lastSavedKeys: [], // track what the server currently has
    };
    this.saveTimeout = null;
  }

  componentDidMount() {
    this.loadData();
  }

  componentWillUnmount() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
  }

  loadData() {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/content_library?action=get_restrictions&content_id=${this.props.content_id}`;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then(res => res.json())
      .then((result) => {
        const raw = result.restrictions || {};
        const parsed = {};
        Object.entries(raw).forEach(([key, data]) => {
          if (typeof data === 'object' && data !== null) {
            parsed[key] = {
              mode: data.mode || 'include',
              values: Array.isArray(data.values) ? data.values : []
            };
          }
        });
        this.setState({
          restrictions: parsed,
          lastSavedKeys: Object.keys(parsed), // remember what server has
        });
      });
  }

  toggleValue = (key, value) => {
    this.setState((prev) => {
      const current = prev.restrictions[key] || { mode: 'include', values: [] };
      const exists = current.values.includes(value);
      const values = exists
        ? current.values.filter(v => v !== value)
        : [...current.values, value];

      const next = { ...prev.restrictions };
      if (values.length === 0) {
        // If no values left, remove the whole restriction
        delete next[key];
      } else {
        next[key] = { ...current, values };
      }

      this.scheduleSave(next);
      return { restrictions: next };
    });
  };

  toggleMode = (key) => {
    this.setState((prev) => {
      const current = prev.restrictions[key] || { mode: 'include', values: [] };
      const newMode = current.mode === 'include' ? 'exclude' : 'include';
      const next = {
        ...prev.restrictions,
        [key]: { ...current, mode: newMode }
      };
      this.scheduleSave(next);
      return { restrictions: next };
    });
  };

  clearRestriction = (key) => {
    this.setState((prev) => {
      const next = { ...prev.restrictions };
      delete next[key];
      this.scheduleSave(next);
      return { restrictions: next };
    });
  };

  scheduleSave = (restrictions) => {
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(() => {
      this.handleSave(restrictions);
    }, 1000);
  };

  handleSave = (restrictions) => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    const params = new URLSearchParams();
    params.append('action', 'set_restrictions');
    params.append('content_id', this.props.content_id);

    // 1) send current restrictions
    Object.entries(restrictions).forEach(([key, data]) => {
      if (Array.isArray(data.values) && data.values.length > 0) {
        data.values.forEach((value) => {
          params.append(`restriction.${key}`, value);
        });
        if (data.mode) {
          params.append(`restriction.${key}.mode`, data.mode);
        }
      }
      // If a key has 0 values, don't send it; it will be cleared below if needed.
    });

    // 2) send explicit clears (keys that were on the server but are now gone)
    const currentKeys = Object.keys(restrictions);
    const removedKeys = this.state.lastSavedKeys.filter(k => !currentKeys.includes(k));
    removedKeys.forEach((k) => {
      params.append('clear_restriction', k); // backend should delete this key
    });

    const url = `${ETVConstants.getServerBase()}/content_library?${params.toString()}`;

    fetch(url, {
      method: 'GET',
      headers: { user_id: reqUserID, access_token: accessToken }
    })
      .then(() => {
        // after a successful save, the server state matches `restrictions`
        this.setState({ lastSavedKeys: currentKeys });
      })
      .catch(() => {
        // optional: keep lastSavedKeys unchanged on error
      });
  };

  render() {
    const { restrictions } = this.state;
    const countryCode = localStorage.getItem('etv.country_code') || 'SG';
    const options = getRestrictionOptions(countryCode);

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          {ETVConstants.trans('restrictions')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {ETVConstants.trans('restrictions_expl')}
        </Typography>

        <Grid container spacing={3}>
          {Object.entries(options).map(([key, config]) => {
            const mode = restrictions[key]?.mode || 'include';
            const modeChecked = mode === 'include';
            const chipColor = modeChecked ? 'success' : 'error';
            const dividerText = modeChecked ? 'Include Values' : 'Exclude Values';

            return (
              <Grid item xs={12} md={6} key={key} sx={{ display: 'flex' }}>
                <Card variant="outlined" sx={{ height: '100%', minHeight: 280, display: 'flex', flexDirection: 'column' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" gutterBottom>{config.label}</Typography>
                      <IconButton
                        aria-label="delete"
                        color="error"
                        onClick={() => this.clearRestriction(key)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {config.description}
                    </Typography>
                    <Divider
                      textAlign="center"
                      sx={{ mb: 2, typography: 'body2', color: 'text.secondary' }}
                    >
                      {dividerText}
                    </Divider>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 1, mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={modeChecked}
                            onChange={() => this.toggleMode(key)}
                            color="success"
                          />
                        }
                        label={modeChecked ? 'Include' : 'Exclude'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {config.values.map((option) => {
                        const selected = restrictions[key]?.values?.includes(option.value);
                        return (
                          <Chip
                            key={option.value}
                            label={option.label}
                            variant={selected ? 'filled' : 'outlined'}
                            color={selected ? chipColor : 'default'}
                            icon={selected ? (modeChecked ? <CheckCircleIcon /> : <CancelIcon />) : null}
                            onClick={() => this.toggleValue(key, option.value)}
                          />
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }
}

export default RestrictionSettings;
